import { openWithTenant } from '@app-store'
import { Modal, CustomButton } from '@ui-kit'
import { ListingTable } from '../listing-table/ListingTable'
import { DuplicatePopupModalProps } from '../../utils/types'
import { useState, useEffect, useMemo, memo, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import {
  attachCaseById,
  patchAttachForHcpEntity,
  fetchTypeStatusForCommunications,
  fetchDocumentIdForHcpEntity,
  fetchCaseDocumentIdForHcpEntity,
  addToaster,
  hideLoader,
  showLoader,
  useTenantNavigate,
} from '@app-store'
import {
  COMM_TEMPLATE,
  DUPLICATE_POPUP,
  DuplicatePopupModalStep,
  NAMESPACES,
} from '../../utils/constants'
import {
  AttachCaseConfirmDialog,
  AttachCaseConfirmDialogHandle,
  ConfirmDialogHandle,
  ProceedAnywayConfirmDialog,
} from './DedupeConfirmDialogs'
import { DedupeOutcomeDetailsStep } from './DedupeOutcomeDetailsStep'
import { useCloseDedupeCaseEnabled } from './dedupe-close-case/useCloseDedupeCaseEnabled'
import { executeDedupeOutcomeSaveFlow } from './dedupe-close-case/executeDedupeOutcomeSaveFlow'
import { v4 as uuid } from 'uuid'
import { QUEUE_STRINGS } from '../../utils/queueIntake.types'
import {
  fetchEntityIdByName,
  fetchLayoutIdByEntityId,
} from '../../utils/helperFunctions'
import { STRINGS } from '../../utils/strings'
import { caseCancelOrVoidApi } from '../../apis/caseStatus.api'
import {
  editEntityData,
  editEntityDataEmbeddedAPI,
  getEntityDataByIdEmbedded,
} from '../../apis/entityData.api'
import { FieldValues } from 'react-hook-form'

export const DuplicatePopupModal = memo(
  ({
    isOpen,
    embedded = false,
    title = '',
    totalResults = 0,
    dataFetchCallback,
    entityId,
    entityName,
    layout,
    extraColumns = [],
    extraActions = [],
    onProceedAnyway,
    onCancel,
    closeModalOnOutsideClick = true,
    isLoading = false,
    updateStatusToProcessing,
    updateStatusToCompleted,
    communicationId,
    faxDetails,
    intakeEntityName,
    fromInteraction,
    proceedButtonText,
  }: DuplicatePopupModalProps) => {
    const dispatch = useAppDispatch()
    const navigate = useTenantNavigate()
    const {
      entityid,
      layoutid,
      id: queueId,
    } = useParams<{
      entityid?: string
      layoutid?: string
      id?: string
    }>()
    const authUser = useAppSelector((state) => state.authUser)
    const { shouldShowCloseDedupeFlow } = useCloseDedupeCaseEnabled(embedded)
    const attachConfirmRef = useRef<AttachCaseConfirmDialogHandle>(null)
    const proceedConfirmRef = useRef<ConfirmDialogHandle>(null)
    const [modalStep, setModalStep] = useState<DuplicatePopupModalStep>(
      DUPLICATE_POPUP.MODAL_STEP.DUPLICATES
    )
    const [pendingAttachCaseId, setPendingAttachCaseId] = useState<
      string | number | null
    >(null)
    const [isSavingOutcomeDetails, setIsSavingOutcomeDetails] = useState(false)
    const categoryData = useAppSelector(
      (state) => state?.categoryDropdown?.categoryMap
    )
    const getCaseDocumentTypeId = () =>
      categoryData?.[COMM_TEMPLATE.CASE_DOCUMENT_TYPE]?.find(
        (item) => item.value === DUPLICATE_POPUP.INTAKE_FORM
      )?.id

    const workflowInstance = useAppSelector(
      (state) => state?.workflowData?.workflowInstance
    )
    const workflowInstanceId = workflowInstance?.id
    const caseId = workflowInstance?.case_id
    const workflowDefinition = useAppSelector(
      (state) => state?.workflowData?.workflowDefinition
    )
    const caseData = useAppSelector((state) => state?.workflowData?.caseData)
    const serviceId = caseData?.services?.id ?? workflowDefinition?.service_id
    const subServiceId =
      (caseData as FieldValues | undefined)?.c_sub_services?.id ??
      workflowDefinition?.global_variables?.inherited_variables
        ?.sub_service_id ??
      workflowDefinition?.definition?.processVariables?.inherited_variables
        ?.sub_service_id
    const [layoutIdForHcpEntity, setLayoutIdForHcpEntity] = useState<number>()

    const fetchWebEnrollmentStatusId = async (): Promise<
      string | undefined
    > => {
      const response = await fetchTypeStatusForCommunications(
        NAMESPACES.WEB_ENROLLMENT_STATUS
      )
      return response?.data?.find(
        (item: { value: string }) =>
          item?.value?.toLowerCase() === NAMESPACES?.PROCESSING_LOWERCASE
      )?.id
    }

    const fetchCaseDocumentId = async (
      id: number | string
    ): Promise<string | undefined> => {
      const entityData = await fetchEntityIdByName(
        dispatch,
        NAMESPACES?.WEB_ENROLLMENT_DOCUMENT
      )
      if (!entityData) return

      const documents = await fetchDocumentIdForHcpEntity(
        entityData?.id.toString() ?? '',
        faxDetails?.enrollment_id ?? ''
      )

      const matchedDoc = documents?.data?.content?.find(
        (item: { enrollment_id: string }) =>
          item?.enrollment_id === faxDetails?.enrollment_id
      )

      if (!matchedDoc?.document?.id) return

      const caseDocEntity = await fetchEntityIdByName(
        dispatch,
        NAMESPACES?.CASE_DOCUMENTS
      )
      if (!caseDocEntity) return

      const layout = await fetchLayoutIdByEntityId(
        dispatch,
        caseDocEntity.id,
        QUEUE_STRINGS?.EDIT_MODE
      )
      const layoutId = layout?.find(
        (item: { entity_name: string }) =>
          item?.entity_name === NAMESPACES.CASE_DOCUMENTS
      )?.id

      if (!layoutId) return

      const caseDocumentTypeId = getCaseDocumentTypeId()
      if (!caseDocumentTypeId) return

      const caseDocument = await fetchCaseDocumentIdForHcpEntity(
        caseDocEntity.id.toString(),
        layoutId.toString(),
        id,
        matchedDoc.document.id.toString(),
        faxDetails?.enrollment_id,
        caseDocumentTypeId
      )

      return caseDocument?.data?.id
    }

    const showSuccessToast = () => {
      dispatch(
        addToaster({
          message: QUEUE_STRINGS?.CASE_ATTACHED,
          type: 'success',
          id: uuid(),
        })
      )
    }

    const handleHcpEntityAttachment = async (id: string | number) => {
      const statusId = await fetchWebEnrollmentStatusId()
      if (!statusId) return

      const attached = await patchAttachForHcpEntity(
        [communicationId ?? ''],
        entityId.toString() ?? '',
        layoutIdForHcpEntity?.toString() ?? '',
        id,
        statusId
      )

      if (!attached) return

      showSuccessToast()

      await fetchCaseDocumentId(id)
    }

    const handleDefaultEntityAttachment = async (
      id: string | number,
      reason: string,
      reviewer: string
    ) => {
      const res = await attachCaseById({
        faxId: communicationId ?? '',
        caseId: id,
        reason,
        reviewer,
      })

      showSuccessToast()

      const caseDocEntity = await fetchEntityIdByName(
        dispatch,
        NAMESPACES?.CASE_DOCUMENTS
      )
      if (!caseDocEntity) return

      const layout = await fetchLayoutIdByEntityId(
        dispatch,
        caseDocEntity.id,
        QUEUE_STRINGS?.EDIT_MODE
      )
      const layoutId = layout?.find(
        (item: { entity_name: string }) =>
          item?.entity_name === NAMESPACES?.CASE_DOCUMENTS
      )?.id

      if (!layoutId) return

      const caseDocumentTypeId = getCaseDocumentTypeId()
      if (!caseDocumentTypeId) return

      await editEntityData(
        NAMESPACES?.CASE_DOCUMENTS,
        caseDocEntity.id.toString(),
        layoutId.toString(),
        {
          case_document_type: {
            id: caseDocumentTypeId,
            _state: NAMESPACES?.CHANGED,
          },
        },
        res?.data?.document_entities?.id,
        []
      )
    }

    const setCaseCompletionDateApi = async () => {
      try {
        await editEntityDataEmbeddedAPI(
          workflowDefinition?.global_variables?.entity_name ?? '',
          Number(caseId),
          { case_completion_date: new Date().toISOString() },
          ['case_completion_date']
        )
      } catch (error) {
        console.error(error)
      }
    }

    const closeCurrentCaseAfterAttach = async () => {
      try {
        dispatch(showLoader())
        await caseCancelOrVoidApi(
          workflowInstanceId?.toString(),
          STRINGS.CANCEL
        )
        const response = await getEntityDataByIdEmbedded(
          workflowDefinition?.global_variables?.entity_name ?? '',
          Number(workflowDefinition?.global_variables?.entity_id),
          ['case_completion_date'],
          Number(caseId)
        )
        if (!response?.data?.case_completion_date) {
          await setCaseCompletionDateApi()
        }
      } catch (error) {
        console.error(error)
        dispatch(
          addToaster({
            message: STRINGS?.SOMETHING_WENT_WRONG,
            type: 'error',
            id: uuid(),
          })
        )
      } finally {
        dispatch(hideLoader())
      }
    }

    const attachCaseForCloseDedupeFlow = async (
      id: string | number,
      reason: string
    ) => {
      const reviewer = `${authUser?.user?.first_name} ${authUser?.user?.last_name}`
      if (intakeEntityName === QUEUE_STRINGS?.HCP_PORTAL_ENTITY_LOWERCASE) {
        await handleHcpEntityAttachment(id)
      } else {
        await handleDefaultEntityAttachment(id, reason, reviewer)
      }
    }

    const onSubmitAttach = async (id: string | number) => {
      try {
        dispatch(showLoader())
        const reviewer = `${authUser?.user?.first_name} ${authUser?.user?.last_name}`
        if (intakeEntityName === QUEUE_STRINGS?.HCP_PORTAL_ENTITY_LOWERCASE) {
          await handleHcpEntityAttachment(id)
        } else {
          await handleDefaultEntityAttachment(
            id,
            NAMESPACES.NO_REASON,
            reviewer
          )
        }
        await updateStatusToProcessing?.(Number(id))
      } catch (err) {
        console.error(err)
      } finally {
        await closeCurrentCaseAfterAttach()
        openWithTenant(
          `/entity/32/workflow/review/${id}?tabs=Correspondence`,
          '_self'
        )
        dispatch(hideLoader())
      }
    }

    const resetOutcomeDetailsStep = useCallback(() => {
      setModalStep(DUPLICATE_POPUP.MODAL_STEP.DUPLICATES)
      setPendingAttachCaseId(null)
      setIsSavingOutcomeDetails(false)
    }, [])

    const handleAttachClick = (row: FieldValues) => {
      if (shouldShowCloseDedupeFlow) {
        attachConfirmRef.current?.open(row?.id)
        return
      }

      onSubmitAttach(row?.id)
    }

    const handleAttachConfirmed = useCallback((caseId: string | number) => {
      setPendingAttachCaseId(caseId)
      setModalStep(DUPLICATE_POPUP.MODAL_STEP.OUTCOME_DETAILS)
    }, [])

    const handleOutcomeDetailsSave = useCallback(
      async ({
        outcomeId,
        outcomeReasonId,
        outcomeReasonLabel,
      }: {
        outcomeId: string
        outcomeReasonId: string
        outcomeReasonLabel: string
      }) => {
        if (
          !shouldShowCloseDedupeFlow ||
          modalStep !== DUPLICATE_POPUP.MODAL_STEP.OUTCOME_DETAILS ||
          pendingAttachCaseId == null
        ) {
          return
        }

        setIsSavingOutcomeDetails(true)

        try {
          dispatch(showLoader())
          await executeDedupeOutcomeSaveFlow({
            attachedCaseId: pendingAttachCaseId,
            outcomeId,
            outcomeReasonId,
            outcomeReasonLabel,
            attachCase: attachCaseForCloseDedupeFlow,
            updateStatusToCompleted,
            faxDetails,
            communicationId,
            caseDocumentTypeId: getCaseDocumentTypeId(),
            assigneeUserId: Number(authUser?.user?.id),
            workflowInstanceId: workflowInstanceId?.toString(),
            caseId,
            workflowDefinition,
            navigate,
            queueParams: { entityid, layoutid, id: queueId },
            requestorId:
              (caseData as FieldValues | undefined)?.requestor_view?.id ?? null,
            tenantId: authUser?.selectedClientTenant?.id ?? null,
            onTaskCreationError: () => {
              dispatch(
                addToaster({
                  message: STRINGS.ERROR_CREATING_TASK,
                  type: 'error',
                  id: uuid(),
                })
              )
            },
          })
        } catch (error) {
          console.error(error)
          dispatch(
            addToaster({
              message: STRINGS.SOMETHING_WENT_WRONG,
              type: 'error',
              id: uuid(),
            })
          )
        } finally {
          dispatch(hideLoader())
          setIsSavingOutcomeDetails(false)
        }
      },
      [
        attachCaseForCloseDedupeFlow,
        authUser?.user?.id,
        caseId,
        dispatch,
        faxDetails,
        modalStep,
        pendingAttachCaseId,
        shouldShowCloseDedupeFlow,
        updateStatusToCompleted,
        workflowDefinition,
        workflowInstanceId,
      ]
    )

    const handleProceedAnywayClick = useCallback(() => {
      if (shouldShowCloseDedupeFlow) {
        proceedConfirmRef.current?.open()
        return
      }

      onProceedAnyway?.()
    }, [shouldShowCloseDedupeFlow, onProceedAnyway])

    const handleProceedAnywayConfirmed = useCallback(() => {
      onProceedAnyway?.()
    }, [onProceedAnyway])

    const handleCancelClick = useCallback(() => {
      if (attachConfirmRef.current?.isOpen()) {
        attachConfirmRef.current.dismiss()
        return
      }

      if (proceedConfirmRef.current?.isOpen()) {
        proceedConfirmRef.current.dismiss()
        return
      }

      if (modalStep === DUPLICATE_POPUP.MODAL_STEP.OUTCOME_DETAILS) {
        resetOutcomeDetailsStep()
        return
      }

      onCancel()
    }, [modalStep, onCancel, resetOutcomeDetailsStep])

    useEffect(() => {
      if (!isOpen) {
        resetOutcomeDetailsStep()
      }
    }, [isOpen, resetOutcomeDetailsStep])

    useEffect(() => {
      const fetchLayoutIdForHcpEntity = async () => {
        const response = await fetchLayoutIdByEntityId(
          dispatch,
          entityId,
          QUEUE_STRINGS?.EDIT_MODE
        )
        const layoutIdForHcpEntity = response?.find(
          (item: { entity_name: string }) =>
            item.entity_name === QUEUE_STRINGS.HCP_PORTAL_ENTITY_LOWERCASE
        )?.id
        layoutIdForHcpEntity && setLayoutIdForHcpEntity(layoutIdForHcpEntity)
      }
      if (
        entityId &&
        intakeEntityName === QUEUE_STRINGS.HCP_PORTAL_ENTITY_LOWERCASE
      ) {
        fetchLayoutIdForHcpEntity()
      }
    }, [entityId, dispatch, intakeEntityName])

    const listingExtraActions = useMemo(() => {
      const hasAttachKeyAction = extraActions.some(
        (item) => item.key === DUPLICATE_POPUP?.ATTACH_KEY
      )

      if (!hasAttachKeyAction) {
        return extraActions
      }

      return extraActions.map((item) =>
        item.key === DUPLICATE_POPUP?.ATTACH_KEY
          ? {
              ...item,
              onClick: (row: FieldValues) => handleAttachClick(row),
            }
          : item
      )
    }, [extraActions, intakeEntityName, layoutIdForHcpEntity, communicationId])

    if (!embedded && !isOpen) {
      return null
    }

    const duplicatesContent = (
      <div className="bg-accent-0 flex flex-col h-full">
        <div className="flex justify-between items-center pb-2">
          <div className="flex items-baseline gap-2">
            <div className="text-sm font-primary font-medium">{title}</div>
            <div className="text-xs font-primary font-light">
              {totalResults} {DUPLICATE_POPUP.RESULT}
              {totalResults !== 1 && 's'} {DUPLICATE_POPUP.FOUND}
            </div>
          </div>
          <div className="flex gap-2.5">
            <CustomButton onClick={handleCancelClick}>
              {NAMESPACES?.CANCEL_BUTTON}
            </CustomButton>
            <CustomButton isFilled onClick={handleProceedAnywayClick}>
              {proceedButtonText || DUPLICATE_POPUP?.PROCEED_ANYWAY}
            </CustomButton>
          </div>
        </div>
        <div className="text-xs font-primary font-light">
          {DUPLICATE_POPUP.SELECTING}{' '}
          <span className="font-medium">
            {fromInteraction ? DUPLICATE_POPUP.LINK : NAMESPACES.ATTACH}
          </span>{' '}
          {fromInteraction
            ? DUPLICATE_POPUP.LINK_SUBTITLE_MSG
            : DUPLICATE_POPUP.ATTACH_SUBTITLE_MSG}
        </div>

        <div className="mt-2.5 border rounded-md p-2 flex flex-col flex-1 min-h-0">
          <ListingTable
            entityId={entityId}
            entityName={entityName}
            layout={layout}
            dataFetchCallback={dataFetchCallback}
            extraColumns={extraColumns}
            extraActions={listingExtraActions}
            hideFilters={true}
            openInNewTab={true}
            isLoading={isLoading}
            isSortable={false}
          />
        </div>
      </div>
    )

    const showOutcomeDetails =
      shouldShowCloseDedupeFlow &&
      modalStep === DUPLICATE_POPUP.MODAL_STEP.OUTCOME_DETAILS

    if (embedded) {
      return duplicatesContent
    }

    return (
      <>
        <Modal
          isOpen={isOpen}
          closeModal={handleCancelClick}
          width="min-w-[60vw] max-w-[70vw]"
          showCloseButton={false}
          closeModalOnOutsideClick={
            showOutcomeDetails ? false : closeModalOnOutsideClick
          }
          isDragable
        >
          {showOutcomeDetails ? (
            <DedupeOutcomeDetailsStep
              serviceId={serviceId}
              subServiceId={subServiceId}
              onBack={resetOutcomeDetailsStep}
              onCancel={handleCancelClick}
              onSave={handleOutcomeDetailsSave}
              isSaving={isSavingOutcomeDetails}
            />
          ) : (
            duplicatesContent
          )}
        </Modal>

        {shouldShowCloseDedupeFlow && (
          <>
            <AttachCaseConfirmDialog
              ref={attachConfirmRef}
              onConfirm={handleAttachConfirmed}
            />
            <ProceedAnywayConfirmDialog
              ref={proceedConfirmRef}
              onConfirm={handleProceedAnywayConfirmed}
            />
          </>
        )}
      </>
    )
  }
)

DuplicatePopupModal.displayName = 'DuplicatePopupModal'
